'use client';

import type { DetectedEnvironment } from '@/lib/code-sessions/environment-detection';
import type { LeftOutVariable } from '@/lib/code-sessions/environment-suggestion';
import type { SecretsSetupItem } from '@/lib/code-sessions/secrets-setup-items';
import type {
  CodeRepositoryCredentialInput,
  CodeRepositoryEnvironmentResponse,
  CodeRepositoryNetworkSettings,
} from '@/lib/code-sessions/types';
import {
  queryOptions,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { codeSessionRequest } from './runtime/cloud';

/** The repository a /code environment belongs to. */
export type CodeRepositoryRef = { installationId: number; repositoryId: number; fullName: string };

type RepositoryScope = Pick<CodeRepositoryRef, 'installationId' | 'repositoryId'>;

/** A session's repository, from its saved row or its create input, which name it `repoFullName`. */
export function repositoryRefOf(
  source: RepositoryScope & { repoFullName: string },
): CodeRepositoryRef {
  return {
    installationId: source.installationId,
    repositoryId: source.repositoryId,
    fullName: source.repoFullName,
  };
}

const ENVIRONMENT_URL = '/api/code-sessions/environment';
const DETECTED_URL = '/api/code-sessions/environment/detected';
const CREDENTIALS_URL = '/api/code-sessions/environment/credentials';
const SECRETS_URL = '/api/code-sessions/environment/secrets';
const VARIABLES_URL = '/api/code-sessions/environment/variables';

export const codeEnvironmentKeys = {
  detail: (repository: RepositoryScope) =>
    ['code-repository-environment', repository.installationId, repository.repositoryId] as const,
  detected: (repository: RepositoryScope) =>
    [
      'code-repository-detected-environment',
      repository.installationId,
      repository.repositoryId,
    ] as const,
  /** Every change to the environment, as a mutation key. */
  changes: (repository: RepositoryScope) =>
    [
      'code-repository-environment-change',
      repository.installationId,
      repository.repositoryId,
    ] as const,
};

function scopeQuery(repository: RepositoryScope, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    installationId: String(repository.installationId),
    repositoryId: String(repository.repositoryId),
    ...extra,
  });
}

function fetchCodeRepositoryEnvironment(repository: RepositoryScope, signal?: AbortSignal) {
  return codeSessionRequest<CodeRepositoryEnvironmentResponse>(
    `${ENVIRONMENT_URL}?${scopeQuery(repository)}`,
    { signal },
  );
}

/**
 * Always refetched: the dialog shows its form only from a fresh copy, and
 * untouched fields follow later refetches.
 */
export function codeEnvironmentOptions(repository: RepositoryScope) {
  return queryOptions({
    queryKey: codeEnvironmentKeys.detail(repository),
    queryFn: ({ signal }) => fetchCodeRepositoryEnvironment(repository, signal),
    staleTime: 0,
  });
}

export function useCodeRepositoryEnvironment(
  repository: CodeRepositoryRef,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({ ...codeEnvironmentOptions(repository), enabled });
}

/**
 * The setup detected from the repository's default branch, or null when
 * nothing was recognized or GitHub could not be read. The server keeps it per
 * commit, so a few minutes' reuse here only delays a new commit's detection.
 */
export function useDetectedCodeEnvironment(
  repository: RepositoryScope,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: codeEnvironmentKeys.detected(repository),
    queryFn: ({ signal }) =>
      codeSessionRequest<{ detected: DetectedEnvironment | null }>(
        `${DETECTED_URL}?${scopeQuery(repository)}`,
        { signal },
      ).then((response) => response.detected),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * Every change answers with the saved environment, which replaces the cached
 * one. Changes can be in flight together and answer out of order, so each then
 * refetches the environment to bring the cache back to what was saved last.
 */
function useEnvironmentMutation<TInput>(
  repository: CodeRepositoryRef,
  mutationFn: (input: TInput) => Promise<CodeRepositoryEnvironmentResponse>,
) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: codeEnvironmentKeys.changes(repository),
    mutationFn,
    onMutate: (): RepositoryScope => ({
      installationId: repository.installationId,
      repositoryId: repository.repositoryId,
    }),
    onSuccess: (response, _input, scope) => {
      client.setQueryData(codeEnvironmentKeys.detail(scope), response);
      void client.invalidateQueries({ queryKey: codeEnvironmentKeys.detail(scope) });
    },
  });
}

/**
 * Whether a change to the repository's environment is still saving, from any
 * section: the dialog stays open until it lands, so its error can show.
 */
export function useIsSavingCodeEnvironment(repository: RepositoryScope) {
  return useIsMutating({ mutationKey: codeEnvironmentKeys.changes(repository) }) > 0;
}

function sendJson(
  url: string,
  method: 'POST' | 'PUT' | 'PATCH',
  repository: RepositoryScope,
  body: object,
) {
  return codeSessionRequest<CodeRepositoryEnvironmentResponse>(url, {
    method,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      installationId: repository.installationId,
      repositoryId: repository.repositoryId,
      ...body,
    }),
  });
}

export function useSaveCodeRepositoryEnvironment(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(
    repository,
    (input: { setupScript: string; startInstructions: string }) =>
      sendJson(ENVIRONMENT_URL, 'PUT', repository, input),
  );
}

/** Sets variables by name and value, as plain or secret ones. */
export function useSetCodeRepositoryVariables(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(
    repository,
    (input: { variables: { name: string; value: string }[]; secret: boolean }) =>
      sendJson(VARIABLES_URL, 'PUT', repository, input),
  );
}

export function useRemoveCodeRepositoryVariable(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(repository, (name: string) =>
    codeSessionRequest<CodeRepositoryEnvironmentResponse>(
      `${VARIABLES_URL}?${scopeQuery(repository, { name })}`,
      { method: 'DELETE' },
    ),
  );
}

export function useSaveCodeRepositoryNetwork(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(repository, (settings: CodeRepositoryNetworkSettings) =>
    sendJson(`${ENVIRONMENT_URL}/network`, 'PUT', repository, settings),
  );
}

/** Adds hosts to the allowlist; the server merges them into the saved ones. */
export function useAllowCodeRepositoryHosts(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(repository, (hosts: readonly string[]) =>
    sendJson(`${ENVIRONMENT_URL}/network`, 'PATCH', repository, {
      addAllowedOutboundHosts: hosts,
    }),
  );
}

/**
 * Saves a suggested environment, or one review page's part of it: the script
 * and start instructions it gives, a blank one keeping the saved one, and its
 * variables, which the server sets in place in the saved ones, so a value
 * saved since the review read the environment stays.
 */
export function useSaveEnvironmentSuggestion(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(
    repository,
    (input: {
      setupScript: string;
      startInstructions: string;
      variables: readonly { name: string; value: string }[];
    }) => sendJson(ENVIRONMENT_URL, 'PATCH', repository, input),
  );
}

/**
 * Leaves suggested variables out of the environment, which removes a
 * suggested value one holds, given with it, or adds them back for the next
 * Save to set.
 */
export function useLeaveOutCodeRepositoryVariables(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(
    repository,
    (input: { variables: readonly LeftOutVariable[]; leftOut: boolean }) =>
      sendJson(`${ENVIRONMENT_URL}/left-out`, 'PATCH', repository, input),
  );
}

/**
 * Saves the secret variables and credentials an installation admin typed, as
 * a secrets upload stores them, in one request: each value with the setup
 * item it is for.
 */
export function useSaveCodeRepositorySecrets(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(
    repository,
    (entries: readonly { item: SecretsSetupItem; value: string }[]) =>
      sendJson(SECRETS_URL, 'PATCH', repository, { entries }),
  );
}

export function useAddCodeRepositoryCredential(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(repository, (input: CodeRepositoryCredentialInput) =>
    sendJson(CREDENTIALS_URL, 'POST', repository, input),
  );
}

export function useDeleteCodeRepositoryCredential(repository: CodeRepositoryRef) {
  return useEnvironmentMutation(repository, (credentialId: string) =>
    codeSessionRequest<CodeRepositoryEnvironmentResponse>(
      `${CREDENTIALS_URL}?${scopeQuery(repository, { id: credentialId })}`,
      { method: 'DELETE' },
    ),
  );
}
